import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { unwrapResult } from '@reduxjs/toolkit';

import { Persistor } from '../../../..';
import { NavContext } from '../../../../App';
import SAVIcon from '../../../../GlobalComponents/IconComponents/SAVIcon';
import signalRService from '../../../../GlobalComponents/Utility/SignalRConnection';
import { setPaginationModelSAVTT } from '../../../../ReduxTLK/features/Pagination/PaginationsSlice';
import { setFilterModel } from '../../../../ReduxTLK/features/SAV/FilterSlice';
import { resetEditTicketDraftByIndex } from '../../../../ReduxTLK/features/SAV/NewTicketDraftSlice';
import { fetchFilteredTickets } from '../../../../ReduxTLK/features/SAV/TicketSlice';
import { addTab, setLastActiveTab } from '../../../../ReduxTLK/features/Tabs/TabsSlice';
import { fetchUsersAgentSAV } from '../../../../ReduxTLK/features/User/UserSlice';
import routes from '../../../../routes';
import EditSAVTicket from '../../../../components/EditSAVTicket/EditSAVTicket';
import ViewSAVTicket from '../../TicketDetails/View/ViewSAVTicket';
import CustomFooterComponent from '../CustomFooterComponent';
import ExecuteTicketModal from '../ExecuteTicketModal';
import Filter from '../Filter/Filter';
import { createColumns } from '../TicketColumn';
import { ticketService } from '../../../../service/ticketService';

import styles from './ListAllTickets.module.css';

const initialNbrsOfTickets = {
  nbrTicketsTotal: 0,
  nbrOuverts: 0,
  nbrEnCours: 0,
  nbrResolus: 0,
  nbrNonResolu: 0,
  nbrClotures: 0,
  nbrAnnules: 0,
  nbrEnAttente: 0,
};

/* Priority list for responsive columns — kept columns by breakpoint */
const COLUMN_PRIORITY = {
  xs: ['id', 'pointDeVente', 'sujet', 'statut', 'action'],
  sm: ['id', 'pointDeVente', 'sujet', 'date', 'statut', 'action'],
  md: ['id', 'pointDeVente', 'sujet', 'date', 'assigne', 'statut', 'statutIntern', 'action'],
  lg: null, // all columns
};

export default function ListAllTickets() {
  const isXs = useMediaQuery('(max-width:480px)');
  const isSm = useMediaQuery('(max-width:768px)');
  const isMd = useMediaQuery('(max-width:1200px)');
  const isSmallScreen = isSm;

  const context = React.useContext(NavContext);
  const dispatch = useDispatch();

  const [openFiltreBox, setOpenFiltreBox] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [dateBegin, setDateBegin] = useState();
  const [dateEnd, setDateEnd] = useState();
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);

  const [ratios, setRatios] = useState({
    delaiPriseEnChargeMoyen: '--',
    delaiParcouruMoyen: '--',
    delaiResolutionMoyen: '--',
    delaiResolutionParametrageMoyen: '--',
  });
  const [notes, setNotes] = useState({
    moyenne: 0,
    nbrNote1: 0,
    nbrNote2: 0,
    nbrNote3: 0,
    nbrNote4: 0,
    nbrNote5: 0,
  });
  const [stats, setStats] = useState({ parametrage: '--', it: '--', dev: '--' });

  const paginationModelSAVTT = useSelector(
    (state) => state.pagination.paginationModelSAVTT
  );

  const [paginationModelCustom, setPaginationModelCustom] = useState(
    paginationModelSAVTT?.page
      ? paginationModelSAVTT
      : { pageSize: 12, page: 1, nbPage: 1 }
  );

  const [checkboxSelection, setCheckboxSelection] = useState(true);
  const [disableRowSelectionOnClick, setDisableRowSelectionOnClick] = useState(true);

  const savedFilterForm = useSelector((state) => state.filter.filter);
  const fetchFiltredModel = useSelector((state) => state.filter.filterModel);
  const filterBoxState = useSelector((state) => state.filter.filterBox);

  const [ticketList, setTicketList] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState('');
  const [nbrsOfTickets, setNbrsOfTickets] = useState(initialNbrsOfTickets);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  /* ---------- Initial load ---------- */
  useEffect(() => {
    setOpenFiltreBox(filterBoxState);
    dispatch(fetchFilteredTickets(fetchFiltredModel))
      .then(unwrapResult)
      .then((res) => {
        dispatch(
          setFilterModel({
            paginationModel: {
              page: res?.data?.pagination?.currentPage,
              pageSize: res?.data?.pagination?.pageSize,
              nbPage: res?.data?.pagination?.nbPage,
            },
            body: { ...fetchFiltredModel.body },
          })
        );
        setTicketList(res.data.result.tickets);
        setLoading(false);
        Persistor.persist();
      });
  }, []);

  useEffect(() => {
    ticketService
      .getSavRatios()
      .then((data) => setRatios(data))
      .catch((error) => console.error('Error fetching SAV ratios:', error));
  }, []);

  useEffect(() => {
    if (context.tabToBeReloaded?.toString() === 'allTickets') {
      dispatch(fetchFilteredTickets(fetchFiltredModel))
        .then(unwrapResult)
        .then((res) => {
          dispatch(
            setFilterModel({
              paginationModel: {
                page: res?.data?.pagination?.currentPage,
                pageSize: res?.data?.pagination?.pageSize,
                nbPage: res?.data?.pagination?.nbPage,
              },
              body: { ...fetchFiltredModel.body },
            })
          );
          setTicketList(res.data.result.tickets);
          setLoading(false);
          Persistor.persist();
        });
    }
    return () => context.setTabToBeReloaded();
  }, [context.tabToBeReloaded]);

  useEffect(() => {
    dispatch(fetchUsersAgentSAV());
  }, []);

  /* ---------- Data fetching ---------- */
  const fetchData = async (data) => {
    if ('dateCreation' in filterOptions) {
      delete filterOptions['dateCreation'];
    }
    dispatch(fetchFilteredTickets(data))
      .then(unwrapResult)
      .then((res) => {
        dispatch(
          setFilterModel({
            paginationModel: {
              page: res?.data?.pagination?.currentPage,
              pageSize: res?.data?.pagination?.pageSize,
              nbPage: res?.data?.pagination?.nbPage,
            },
            body: { ...fetchFiltredModel.body },
          })
        );

        const {
          nbrTicketsTotal,
          nbrOuverts,
          nbrEnCours,
          nbrResolus,
          nbrNonResolu,
          nbrClotures,
          nbrAnnules,
          nbrEnAttente,
          tickets,
          note,
          stat,
        } = res.data.result;

        setNbrsOfTickets({
          nbrTicketsTotal,
          nbrOuverts,
          nbrEnCours,
          nbrResolus,
          nbrNonResolu,
          nbrClotures,
          nbrAnnules,
          nbrEnAttente,
        });
        setNotes(note);
        setStats(stat);
        setTicketList(tickets);
        setLoading(false);
        Persistor.persist();
      });
  };

  /* ---------- Tab handlers ---------- */
  const newTab = routes && routes[0]?.pages[1]?.views[2];
  const newTabView = routes && routes[0]?.pages[1]?.views[4];

  const handleExecute = (ticket) => {
    setOpenModal(true);
    setSelectedTicket(ticket);
  };

  const handleEdit = (ticket) => openNewTab({ newTab, ticket });
  const handleView = (ticket) => openNewViewTab({ newTabView, ticket });

  function openNewTab(props) {
    setOpenFiltreBox(false);
    const title = '#' + props.ticket;
    const composedId = 'SAV_Ticket_' + props.ticket;
    const existingTabIndex = context.tabs.findIndex((tab) => tab.id === composedId);

    if (existingTabIndex !== -1) {
      context.setActiveTab(composedId);
      dispatch(setLastActiveTab(composedId));
      context.setRoomId(props.ticket);
      signalRService.onJoinRoom(props.ticket);
      return;
    }

    dispatch(resetEditTicketDraftByIndex(props.ticket));
    dispatch(
      addTab({
        module: 'SAV',
        action: 'Edit',
        identifier: props.ticket,
        id: composedId,
        label: title,
      })
    );

    context.setTabs([
      ...context.tabs,
      {
        id: composedId,
        label: title,
        identifier: props.ticket,
        component: (
          <EditSAVTicket
            id={props.ticket}
            fetchData={fetchData}
            paginationModelCustom={paginationModelCustom}
          />
        ),
        closeable: true,
        icon: <SAVIcon />,
      },
    ]);
    Persistor.persist();
    context.setActiveTab(composedId);
    dispatch(setLastActiveTab(composedId));
    context.setRoomId(props.ticket);
    signalRService.onJoinRoom(props.ticket);
  }

  function openNewViewTab(props) {
    setOpenFiltreBox(false);
    const title = '#' + props.ticket;
    const composedId = 'SAV_Ticket_' + props.ticket;
    const existingTabIndex = context.tabs.findIndex((tab) => tab.id === composedId);

    if (existingTabIndex !== -1) {
      context.setActiveTab(composedId);
      dispatch(setLastActiveTab(composedId));
      context.setRoomId(props.ticket);
      signalRService.onJoinRoom(props.ticket);
      return;
    }

    dispatch(
      addTab({
        module: 'SAV',
        action: 'View',
        identifier: props.ticket,
        id: composedId,
        label: title,
        closeable: true,
      })
    );
    Persistor.persist();
    context.setTabs([
      ...context.tabs,
      {
        id: composedId,
        label: title,
        identifier: props.ticket,
        component: <ViewSAVTicket id={props.ticket} />,
        closeable: true,
        icon: <SAVIcon />,
      },
    ]);
    Persistor.persist();
    context.setActiveTab(composedId);
    dispatch(setLastActiveTab(composedId));
    context.setRoomId(props.ticket);
    signalRService.onJoinRoom(props.ticket);
  }

  const handleSelectionChange = (selection) => setSelectedRows(selection);

  /* ---------- Responsive columns ---------- */
  const allColumns = useMemo(
    () => createColumns(handleView, handleEdit, handleExecute, isSmallScreen),
    [isSmallScreen]
  );

  const visibleColumns = useMemo(() => {
    let allowedFields;
    if (isXs) allowedFields = COLUMN_PRIORITY.xs;
    else if (isSm) allowedFields = COLUMN_PRIORITY.sm;
    else if (isMd) allowedFields = COLUMN_PRIORITY.md;
    else allowedFields = null;

    if (!allowedFields) return allColumns;
    return allColumns.filter((col) => allowedFields.includes(col.field));
  }, [allColumns, isXs, isSm, isMd]);

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingContainer}>
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 32, color: '#2A66C6' }} spin />
            }
          />
          <span className={styles.loadingText}>Chargement des tickets...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Filter section: natural height, sits at top */}
      <div className={styles.filterWrap}>
        <Filter
          loading={loading}
          setLoading={setLoading}
          fetchData={fetchData}
          setOpenFiltreBox={setOpenFiltreBox}
          setFilterOptions={setFilterOptions}
          dateBegin={dateBegin}
          dateEnd={dateEnd}
          setDateBegin={setDateBegin}
          setDateEnd={setDateEnd}
          openFiltreBox={openFiltreBox}
          setTicketList={setTicketList}
          setPaginationModelCustom={setPaginationModelCustom}
          paginationModelCustom={paginationModelCustom}
          setPaginationModelSAVTT={setPaginationModelSAVTT}
          nbrsOfTickets={nbrsOfTickets}
          ratios={ratios}
          notes={notes}
          stats={stats}
        />
      </div>

      {/* Grid section: fills all remaining space, scrolls internally */}
      <div className={styles.gridSection}>
        <div className={styles.ticketsContainer}>
          <DataGrid
            className={styles.dataGrid}
            rows={ticketList}
            columns={visibleColumns}
            page={fetchFiltredModel?.paginationModel?.page}
            setPage={setPage}
            pageSize={fetchFiltredModel?.paginationModel?.pageSize}
            disableSelectionOnClick
            selectionModel={selectedRows}
            onSelectionModelChange={(newSelection) =>
              handleSelectionChange(newSelection.selectionModel)
            }
            editMode={false}
            disableColumnSelector
            slots={{
              pagination: false,
              footer: CustomFooterComponent,
              noRowsOverlay: () => (
                <div className={styles.emptyState}>
                  <span className={styles.emptyText}>Aucun ticket à afficher</span>
                </div>
              ),
            }}
            slotProps={{
              footer: { fetchFiltredModel, fetchData },
            }}
            rowHeight={isSm ? 48 : 56}
            columnHeaderHeight={isSm ? 40 : 44}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
          />
        </div>
      </div>

      <ExecuteTicketModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        openTicket={selectedTicket}
        fetchData={fetchData}
        paginationModelCustom={fetchFiltredModel}
      />
    </div>
  );
}